/**
 * @module auditionService
 * @description 캐스팅 공고 API 호출 모듈
 *
 * 백엔드 API: GET /api/castings, GET /api/castings/{id}
 * 프론트엔드 데이터 모델로 변환하는 어댑터 레이어 포함
 */

import { getCastingList, getCastingDetail, apply, uploadFile } from '../api';
import { getProducerById } from '../api/producerApi';
import { isProducerLogoRemoved } from '../pages/Producer/Company/ProducerCompanyPage';
import { formatCastingDDay, isDeadlineClosing, isDeadlineExpired } from '../utils/castingDeadline';

// ── 상태 변환 ─────────────────────────────────────────────────────────────────

const STATUS_TO_FRONTEND = {
  OPEN: 'open',
  PLANNING: 'open',
  IN_PRODUCTION: 'open',
  CLOSED: 'closed',
};

// 목록에서 숨겨야 하는 상태
const HIDDEN_STATUSES = new Set(['DRAFT']);

// ── D-Day 계산 ────────────────────────────────────────────────────────────────

function computeDDay(deadline) {
  return formatCastingDDay(deadline, { closedLabel: 'D-Day', todayLabel: 'D-Day' });
}

function computeIsClosing(deadline) {
  return isDeadlineClosing(deadline);
}

// ── 응답 데이터 어댑터 ────────────────────────────────────────────────────────

function computeIsNew(createdAt) {
  if (!createdAt) return false;
  const diff = (new Date() - new Date(createdAt)) / (1000 * 60 * 60 * 24);
  return diff <= 3;
}

function formatYearMonth(dateValue) {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월`;
}

function formatShootingPeriod(item) {
  const direct =
    item.shootingPeriod ||
    item.shootPeriod ||
    item.filmingPeriod ||
    item.productionPeriod;
  if (direct) return direct;

  const start =
    item.shootStartDate ||
    item.shootingStartDate ||
    item.filmingStartDate ||
    item.productionStartDate;
  const end =
    item.shootEndDate ||
    item.shootingEndDate ||
    item.filmingEndDate ||
    item.productionEndDate;

  const startLabel = formatYearMonth(start);
  const endLabel = formatYearMonth(end);
  if (startLabel && endLabel) {
    return startLabel === endLabel ? startLabel : `${startLabel} ~ ${endLabel}`;
  }
  if (startLabel) return `${startLabel} 예정`;
  if (endLabel) return `${endLabel}까지`;
  return '추후 공지';
}

function mapListItem(item) {
  const province = item.regionProvince || item.shootingLocation || item.location || '';
  const city = item.regionCity || '';
  // 광역시처럼 province === city인 경우 중복 제거
  const locationParts = city && city !== province ? [province, city] : [province];
  const isClosing = computeIsClosing(item.applicationDeadline);
  const isExpired = item.applicationDeadline ? isDeadlineExpired(item.applicationDeadline) : false;
  const backendClosed = item.status === 'CLOSED';
  let status;
  if (isExpired || backendClosed) {
    status = 'closed';
  } else if (isClosing) {
    status = 'closing';
  } else {
    status = STATUS_TO_FRONTEND[item.status] || 'open';
  }
  return {
    id: String(item.id),
    category: item.projectType || item.category || '기타',
    location: locationParts.filter(Boolean).join(' ') || '전국',
    dDay: computeDDay(item.applicationDeadline),
    title: item.title,
    company: item.companyName || '',
    castingDirectorName: item.castingDirectorName || item.managerName || '',
    roles: (item.roles || []).map((r) => r.roleName || r.name || '').filter(Boolean),
    status,
    isNew: computeIsNew(item.createdAt),
    isClosing,
    isExpired: isExpired || backendClosed,
  };
}

function matchesSelectedCategories(item, categories = []) {
  if (!categories.length) return true;
  const itemCategory = item.projectType || item.category || '';
  return categories.includes(itemCategory);
}

function matchesSelectedLocations(item, locations = []) {
  if (!locations.length) return true;

  const province = (item.regionProvince || '').trim();
  const city = (item.regionCity || '').trim();

  // 광역시는 province === city인 경우가 있음 (예: regionProvince="부산", regionCity="부산")
  // 이 경우 city를 빈 문자열로 정규화
  const normalizedCity = city === province ? '' : city;

  return locations.some((selected) => {
    const normalized = selected.trim();
    if (!normalized) return false;

    // 단일 지명 선택 ("서울", "부산" 등)
    if (!normalized.includes(' ')) {
      // province와 직접 일치
      if (province === normalized) return true;
      // city와 직접 일치 (광역시 city 단독 저장 케이스)
      if (normalizedCity === normalized) return true;
      return false;
    }

    // "도 시" 형태 선택 ("경기 수원", "경북 포항")
    const [selProvince, selCity] = normalized.split(' ');
    if (province === selProvince && normalizedCity === selCity) return true;

    return false;
  });
}

function mapDetail(item) {
  const isClosing = computeIsClosing(item.applicationDeadline);
  return {
    id: String(item.id),
    category: item.projectType || item.category || '기타',
    isNew: computeIsNew(item.createdAt),
    status: isClosing ? 'closing' : (STATUS_TO_FRONTEND[item.status] || 'open'),
    isClosing,
    dDay: computeDDay(item.applicationDeadline),
    deadline: item.applicationDeadline || '미정',
    title: item.title,
    company: {
      id: String(item.producerProfileId),
      name: item.companyName || '',
      castingDirectorName: item.castingDirectorName || item.managerName || '',
      isVerified: false,
    },
    payment: (() => {
      const pays = (item.roles || []).map((r) => (r.pay || '').trim()).filter(Boolean);
      if (pays.length === 0) return '협의 후 결정';
      const unique = [...new Set(pays)];
      return unique.length === 1 ? unique[0] : '배역별 상이';
    })(),
    shootingPeriod: formatShootingPeriod(item),
    shootingLocation: (() => {
      const p = item.regionProvince || '';
      const c = item.regionCity || '';
      if (!p && !c) return '전국';
      if (!c || p === c) return p;
      return `${p} ${c}`;
    })(),
    description: item.synopsis || '',
    requirements: item.requirements
      ? item.requirements.split('\n').map((s) => s.replace(/^[-•]\s*/, '').trim()).filter(Boolean)
      : [],
    process: (item.stages || [])
      .sort((a, b) => a.stageNumber - b.stageNumber)
      .map((s) => ({ step: s.stageNumber, label: s.description || '' })),
    additionalInfo: item.additionalInfo || '',
    // 서버가 빈 자리를 null로 채워 보내는 경우가 있다([null]). 그대로 그리면
    // 파일 이름을 뽑다가 터져서 공고 상세가 통째로 빈 화면이 된다.
    attachments: Array.isArray(item.attachments)
      ? item.attachments.filter(url => typeof url === 'string' && url.trim())
      : [],
    roles: (item.roles || [])
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((r) => ({
        id: String(r.id),
        name: r.roleName || '',
        roleType: r.roleType || '',
        gender: r.gender || '무관',
        ageRange:
          r.ageMin != null && r.ageMax != null
            ? `${Math.min(r.ageMin, r.ageMax)}~${Math.max(r.ageMin, r.ageMax)}세`
            : '제한 없음',
        payment: r.pay || '협의 가능',
        shootingPeriod: formatShootingPeriod(r),
        headcount: r.headcount ?? null,
        heightMin: r.heightMin ?? null,
        requiredSkills: r.requiredSkills || '',
        description: r.characterDesc || '',
      })),
    producerInfo: {
      logo: null,
      description: '',
      activeCastings: 0,
      completedProjects: 0,
      website: null,
    },
    isSaved: item.isSaved ?? item.isBookmarked ?? false,
    archiveItemId: item.archiveItemId ?? item.bookmarkItemId ?? null,
    hasApplied: false,
    viewCount: item.viewCount ?? 0,
  };
}

// ── API 함수 ─────────────────────────────────────────────────────────────────

/**
 * 캐스팅 공고 목록 조회
 *
 * @param {Object} params
 * @param {string} params.search    - 검색어
 * @param {string} params.category  - 카테고리 필터
 * @param {string} params.location  - 지역 필터
 * @param {string} params.status    - 상태 필터 ('open' | 'closing' | 'closed')
 * @param {string} params.sort      - 정렬 ('latest' | 'deadline')
 * @param {number} params.page      - 페이지 (1-based)
 * @param {number} params.limit     - 페이지당 항목 수
 * @returns {Promise<{total, page, limit, totalPages, data}>}
 */
// 주의: 백엔드 /api/castings가 아직 실제 서버 페이지네이션(Pageable)을 지원하지 않아
// callpage/perpage를 보내도 무시하고 전체 목록을 반환한다. 그래서 한도(FETCH_CAP)를
// 넉넉히 잡아 한 번에 받아온 뒤 프론트에서 직접 필터링/페이지네이션한다.
// 공고 수가 이 한도를 넘으면 뒤쪽 공고가 누락될 수 있으니, 백엔드가 Pageable을
// 지원하도록 고쳐지면 이 우회 로직은 제거하고 서버 페이지네이션으로 전환해야 한다.
const FETCH_CAP = 1000;
// 원본 목록 캐시 — 검색어/필터/정렬/페이지가 바뀔 때마다 매번 1000개를 다시
// 내려받지 않도록, 짧은 TTL 동안은 같은 원본 목록을 재사용한다.
const RAW_LIST_TTL_MS = 30 * 1000;
let rawListCache = null; // { list, fetchedAt }
let inflightFetch = null;

async function getRawCastingList() {
  const now = Date.now();
  if (rawListCache && now - rawListCache.fetchedAt < RAW_LIST_TTL_MS) {
    return rawListCache.list;
  }
  if (inflightFetch) return inflightFetch;

  inflightFetch = (async () => {
    const res = await getCastingList({ perpage: FETCH_CAP, size: FETCH_CAP, callpage: 1, page: 0 });
    const rawList = Array.isArray(res) ? res : (res.content ?? []);
    if (rawList.length >= FETCH_CAP) {
      // eslint-disable-next-line no-console
      console.warn(`[fetchAuditions] 공고 수가 FETCH_CAP(${FETCH_CAP})에 도달했습니다 — 일부 공고가 누락될 수 있습니다. 백엔드 서버 페이지네이션 적용이 필요합니다.`);
    }
    rawListCache = { list: rawList, fetchedAt: Date.now() };
    return rawList;
  })();

  try {
    return await inflightFetch;
  } finally {
    inflightFetch = null;
  }
}

/** 새 공고 등록/삭제 등으로 목록을 즉시 갱신하고 싶을 때 캐시를 비운다. */
export function invalidateAuditionsCache() {
  rawListCache = null;
}

/** 필터 카운트 계산 등에서 원본 공고 목록이 필요할 때 캐시를 공유해서 재사용한다. */
export async function getCachedRawAuditionList() {
  return getRawCastingList();
}

export async function fetchAuditions({
  search = '',
  category = '',
  location = '',
  status = '',
  sort = 'latest',
  page = 1,
  limit = 9,
  // 1페이지에 광고 카드가 끼어서 "광고 포함 limit개"를 맞춰야 할 때만 다르게 준다.
  // 예: limit=9인데 1페이지는 광고 1개 자리를 비워야 하면 firstPageLimit=8.
  firstPageLimit = limit,
} = {}) {
  const selectedCategories = category ? category.split(',').map((v) => v.trim()).filter(Boolean) : [];
  const selectedLocations = location ? location.split(',').map((v) => v.trim()).filter(Boolean) : [];

  const rawList = await getRawCastingList();
  let list = rawList.filter(item => !HIDDEN_STATUSES.has(item.status));

  // 검색어
  if (search) {
    const kw = search.toLowerCase();
    list = list.filter(item =>
      (item.title || '').toLowerCase().includes(kw) ||
      (item.companyName || '').toLowerCase().includes(kw)
    );
  }

  // 장르 (projectType)
  if (selectedCategories.length > 0) {
    list = list.filter(item => matchesSelectedCategories(item, selectedCategories));
  }

  // 지역
  if (selectedLocations.length > 0) {
    list = list.filter(item => matchesSelectedLocations(item, selectedLocations));
  }

  // 상태
  if (status) {
    if (status === 'closed') {
      list = list.filter(item =>
        item.status === 'CLOSED' ||
        (item.applicationDeadline && isDeadlineExpired(item.applicationDeadline))
      );
    } else if (status === 'closing') {
      list = list.filter(item =>
        item.status !== 'CLOSED' &&
        !(item.applicationDeadline && isDeadlineExpired(item.applicationDeadline)) &&
        computeIsClosing(item.applicationDeadline)
      );
    } else if (status === 'open') {
      list = list.filter(item =>
        item.status !== 'CLOSED' &&
        !(item.applicationDeadline && isDeadlineExpired(item.applicationDeadline)) &&
        !computeIsClosing(item.applicationDeadline)
      );
    }
  }

  // 모집중 여부 판단 헬퍼 (정렬 우선순위에 사용)
  const isActive = (item) =>
    item.status !== 'CLOSED' &&
    !(item.applicationDeadline && isDeadlineExpired(item.applicationDeadline));

  // 정렬
  if (sort === 'deadline') {
    // 마감임박순: 모집중인 공고를 마감일 오름차순으로 앞에, 마감된 공고는 뒤에 최신순
    list = [...list].sort((a, b) => {
      const aActive = isActive(a);
      const bActive = isActive(b);
      if (aActive !== bActive) return aActive ? -1 : 1;
      if (aActive && bActive) {
        const da = a.applicationDeadline ? new Date(a.applicationDeadline) : new Date('9999-12-31');
        const db = b.applicationDeadline ? new Date(b.applicationDeadline) : new Date('9999-12-31');
        return da - db;
      }
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
  } else {
    // latest: 모집중인 공고를 최신순으로 앞에, 마감된 공고는 뒤에 최신순
    list = [...list].sort((a, b) => {
      const aActive = isActive(a);
      const bActive = isActive(b);
      if (aActive !== bActive) return aActive ? -1 : 1;
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
  }

  // 1페이지만 크기가 다를 수 있어서(광고 자리), 이후 페이지는 1페이지 뒤부터 균등하게 나눈다.
  const total = list.length;
  const totalPages = Math.max(1, 1 + Math.ceil(Math.max(0, total - firstPageLimit) / limit));
  const offset = page <= 1 ? 0 : firstPageLimit + (page - 2) * limit;
  const take = page <= 1 ? firstPageLimit : limit;
  const pagedList = list.slice(offset, offset + take);

  // 진행중 공고 수: CLOSED가 아니고 마감일이 지나지 않은 공고
  const activeTotal = list.filter(item =>
    item.status !== 'CLOSED' &&
    !(item.applicationDeadline && isDeadlineExpired(item.applicationDeadline))
  ).length;

  // 담당자 이름: 현재 페이지 항목의 producerProfileId를 모아 병렬 조회
  const producerIds = [...new Set(pagedList.map(i => i.producerProfileId).filter(Boolean))];
  const producerMap = {};
  await Promise.all(
    producerIds.map(id =>
      getProducerById(id)
        .then(p => { producerMap[id] = p.managerName || ''; })
        .catch(() => {})
    )
  );

  return {
    total,
    activeTotal,
    page,
    limit,
    totalPages,
    data: pagedList.map(item => {
      const mapped = mapListItem(item);
      if (!mapped.castingDirectorName && item.producerProfileId && producerMap[item.producerProfileId]) {
        mapped.castingDirectorName = producerMap[item.producerProfileId];
      }
      return mapped;
    }),
  };
}

/**
 * 캐스팅 공고 단건 상세 조회
 *
 * @param {string|number} id
 * @returns {Promise<Object>}
 * @throws {Error} 'NOT_FOUND' — 404 응답 시
 */
export async function fetchAuditionById(id) {
  try {
    const res = await getCastingDetail(id);
    const detail = mapDetail(res);

    // 제작사 프로필(로고 등) 조회
    if (res.producerProfileId) {
      try {
        const producer = await getProducerById(res.producerProfileId);
        detail.producerInfo = {
          ...detail.producerInfo,
          logo: isProducerLogoRemoved() ? null : (producer.logoUrl || null),
          description: producer.companyDescription || producer.description || '',
          activeCastings: producer.activeCastings ?? producer.castingCount ?? 0,
          completedProjects: producer.completedProjects ?? producer.projectCount ?? 0,
        };
        // company.name이 비어있으면 producer API에서 채움
        if (!detail.company.name && (producer.companyName || producer.name)) {
          detail.company = { ...detail.company, name: producer.companyName || producer.name || '' };
        }
        // 담당자 이름: castingDirectorName 없으면 managerName으로 보완
        if (!detail.company.castingDirectorName && producer.managerName) {
          detail.company = { ...detail.company, castingDirectorName: producer.managerName };
        }
      } catch {
        // 제작사 조회 실패 시 기본값 유지
      }
    }

    return detail;
  } catch (err) {
    if (err.response?.status === 404) throw new Error('NOT_FOUND');
    throw err;
  }
}

/**
 * 공고 지원 제출
 *
 * @param {string} auditionId
 * @param {{ roleId, bio, videoLink, files, talentProfileId }} payload
 */
export async function submitAuditionApplication(auditionId, { roleId, bio, videoLink, files, talentProfileId }) {
  const fileIds = files.length > 0
    ? await Promise.all(
        files.map(async f => {
          const fileType = f.type.startsWith('video/') ? 'VIDEO'
            : f.type === 'application/pdf' ? 'DOCUMENT'
            : 'IMAGE';
          const { fileEntityId } = await uploadFile(f, fileType);
          return fileEntityId;
        })
      )
    : [];

  await apply({
    castingPostId: Number(auditionId),
    ...(talentProfileId && { talentProfileId: Number(talentProfileId) }),
    ...(roleId != null && { castingRoleId: Number(roleId) }),
    ...(bio && { message: bio }),
    ...(videoLink && { videoLink }),
    ...(fileIds.length > 0 && { attachmentFileIds: fileIds }),
  });
}
